'use client';

import type { ComponentPropsWithRef, ReactNode } from 'react';
import {
    Root as AccordionRoot,
    Item as AccordionItem,
    Header as AccordionHeader,
    Trigger as AccordionTrigger,
    Content as AccordionContent,
    type AccordionMultipleProps,
} from '@radix-ui/react-accordion';
import { MinusIcon } from '@/shared-ui/icons/minus';
import { PlusIcon } from '@/shared-ui/icons/plus';
import { cn } from '@/lib/utils';
import { Typography, type TypographyProps } from '@/shared-ui/atoms/typography';

type RootProps = Omit<AccordionMultipleProps, 'type'>;

type ItemProps = ComponentPropsWithRef<typeof AccordionItem>;

type TriggerProps = Omit<ComponentPropsWithRef<typeof AccordionTrigger>, 'children'> & {
    children: ReactNode;
};

type ContentProps = ComponentPropsWithRef<typeof AccordionContent> & {
    children: ReactNode;
    accordionContentClassName?: string;
};

type AccordionItemProps = ItemProps & {
    borderColor?: string;
};

type AccordionTriggerProps = TriggerProps & {
    headingId?: string;
    variant?: TypographyProps['variant'];
    weight?: TypographyProps['weight'];
};

const Root = (props: RootProps) => {
    const { className, ...multipleProps } = props;
    return (
        <AccordionRoot
            data-slot="accordion-root"
            className={cn('w-full', className)}
            type="multiple"
            {...multipleProps}
        />
    );
};
Root.displayName = 'Accordion.Root';

const Item = ({ className, style, borderColor, ...props }: AccordionItemProps) => (
    <AccordionItem
        data-slot="accordion-item"
        className={cn('border-b border-current', className)}
        style={{ ...style, borderColor }}
        {...props}
    />
);
Item.displayName = 'Accordion.Item';

const Trigger = ({
    className,
    children,
    headingId,
    variant = 'h4',
    weight = 'regular',
    ...props
}: AccordionTriggerProps) => (
    <AccordionHeader asChild data-slot="accordion-header" className="flex">
        <Typography id={headingId} variant={variant} weight={weight} className="m-0 flex w-full">
            <AccordionTrigger
                data-slot="accordion-trigger"
                className={cn(
                    'group flex w-full items-center justify-between gap-3 py-3 text-left cursor-pointer',
                    '[&[data-state=open]_[data-slot=accordion-plus]]:hidden',
                    '[&[data-state=closed]_[data-slot=accordion-minus]]:hidden',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60',
                    'disabled:cursor-default disabled:pointer-events-none disabled:opacity-50',
                    className
                )}
                {...props}>
                {/* We use another Typography component to ensure the correct styling is inherited from the parent Typography. */}
                <Typography as="span" variant={variant} weight={weight}>
                    {children}
                </Typography>
                <span
                    aria-hidden
                    className="ml-3 inline-flex size-4 items-center justify-center text-base leading-none">
                    <span data-slot="accordion-plus" className="size-4">
                        <PlusIcon />
                    </span>
                    <span data-slot="accordion-minus" className="size-4">
                        <MinusIcon />
                    </span>
                </span>
            </AccordionTrigger>
        </Typography>
    </AccordionHeader>
);
Trigger.displayName = 'Accordion.Trigger';

const Content = ({ className, accordionContentClassName, children, ...props }: ContentProps) => (
    <AccordionContent
        data-slot="accordion-content"
        className={cn(
            'overflow-hidden',
            'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
            className
        )}
        {...props}>
        <div className={cn('pb-3', accordionContentClassName)}>{children}</div>
    </AccordionContent>
);
Content.displayName = 'Accordion.Content';

const Accordion = { Root, Item, Trigger, Content } as const;

export { Accordion };
